import {
    Avatar,
    Box,
    Chip,
    IconButton,
    Typography,
} from '@mui/material';

import { Icon } from '@iconify/react';
import AppMenu from '../../components/AppMenu/AppMenu';

import './MerchantsPage.scss';

const getStatusColor = (status) => {
    switch (status) {
        case 'active':
            return 'success';
        case 'suspended':
            return 'error';
        default:
            return 'default';
    }
};
const capitalize = (str) => str ? str.charAt(0).toUpperCase() + str.slice(1) : '-';

const StatusChip = ({ status }) => {
    return (
        <Box className={`status-chip status-chip--${status.toLowerCase()}`}>
            <Icon icon="ph:circle-fill" height={5} />
            {capitalize(status)}
        </Box>
    )
};

const PaymentChip = ({ payment }) => {
    return (
        <Box className={`status-chip status-chip--${payment?.toLowerCase()}`}>
            <Icon icon="ph:circle-fill" height={5} />
            {capitalize(payment)}
        </Box>
    )
};

const TaxChip = ({ tax }) => {
    return (
        <Box className={`tax-chip tax-chip--${tax ? tax?.toLowerCase() : ''}`}>
            {capitalize(tax)}
        </Box>
    )
};

export default function merchantColumns({
    handleDirectToMerchantDetails,
    handleStatusChange,
}) {

    return [
        {
            field: 'merchant',
            header: 'Merchant',
            minWidth: 320,
            render: (row) => (
                <Box
                    display="flex"
                    alignItems="center"
                    gap={2}
                >
                    <Avatar
                        src={row?.logo}
                        sx={{
                            width: 44,
                            height: 44,
                        }}
                    >
                        {row?.name?.charAt(0)}
                    </Avatar>
                    <Box>
                        <Typography
                            fontWeight={600}
                        >
                            {row?.name}
                        </Typography>
                        <Typography
                            variant="body2"
                            color="text.secondary"
                        >
                            {row?.code}
                        </Typography>
                    </Box>
                </Box>
            ),
        },
        {
            field: 'outlet_count',
            header: 'Outlet',
            align: 'center',
        },
        {
            field: 'status',
            header: 'Status',
            render: value => (
                <StatusChip status={value?.status} />
            ),
        },
        {
            field: 'monthly_gmv',
            header: 'Monthly GMV',
            render: value =>
                `RM ${Number(value?.monthly_gmv || 0).toLocaleString()}`,
        },
        // {
        //     field: 'orders',
        //     header: 'Orders',
        // },
        // {
        //     field: 'wa_credits',
        //     header: 'WA Credits',
        // },
        {
            field: 'last_active',
            header: 'Last Active',
        },
        {
            field: 'actions',
            header: '',
            render: (row) => {
                const STATUS_MENU = {
                    active: [
                        {
                            label: 'View details',
                            icon: 'ph:clipboard',
                            onClick: () => handleDirectToMerchantDetails(row?.id),
                        },
                        {
                            label: 'Suspend',
                            icon: 'ph:minus-circle',
                            onClick: () => handleStatusChange(row),
                            color: 'red',
                        },
                    ],
                    suspended: [
                        {
                            label: 'View details',
                            icon: 'ph:clipboard',
                            onClick: () => handleDirectToMerchantDetails(row?.id),
                        },
                        {
                            label: 'Reactivate',
                            icon: 'ph:check',
                            onClick: () => handleStatusChange(row),
                        },
                    ],
                };
                return (
                    <AppMenu options={STATUS_MENU[row?.status]} />
                );
            }
        },
    ];
}

export const merchantCatalogColumns = [
    {
        field: 'id',
        header: 'ITEM ID',
    },
    {
        field: 'name',
        header: 'NAME',
    },
    {
        field: 'description',
        header: 'DESCRIPTION',
        render: (row) => {
            return row?.description || '-';
        }
    },
    {
        field: 'category',
        header: 'CATEGORY',
        minWidth: 150,
        render: (row) => {
            return row?.categories?.length > 0 ? row?.categories?.map(cat => cat.category_name).join(', ') : '-';
        }
    },
    {
        field: 'unit_price',
        header: 'UNIT PRICE',
        render: (row) => {
            return (<b>{`RM ${row?.price}`}</b>);
        }
    },
    {
        field: 'tax',
        header: 'TAX',
        render: (row) => {
            return <TaxChip tax={row?.tax_class == 1 ? 'inclusive' : 'exclusive' || '-'} />;
        }
    },
    {
        field: 'status',
        header: 'STATUS',
        render: (row) => {
            return <StatusChip status={row?.status || '-'} />;
        }
    },
]

export const merchantOrderColumns = [
    {
        field: 'id',
        header: 'ITEM ID',
    },
    {
        field: 'name',
        header: 'NAME',
    },
    {
        field: 'description',
        header: 'DESCRIPTION',
    },
    {
        field: 'category',
        header: 'CATEGORY',
    },
    {
        field: 'unit_price',
        header: 'UNIT PRICE',
        render: (row) => {
            return (<b>{`RM ${Number(row?.unit_price || 0).toLocaleString()}`}</b>);
        }
    },
    {
        field: 'tax',
        header: 'TAX',
        render: (row) => {
            return <TaxChip tax={row?.tax || '-'} />;
        }
    },
    {
        field: 'status',
        header: 'STATUS',
        render: (row) => {
            return <StatusChip status={row?.status || '-'} />;
        }
    },
]

export const merchantReservationColumns = [
    {
        field: 'id',
        header: 'RESERVATION',
    },
    {
        field: 'name',
        header: 'CUSTOMER',
    },
    {
        field: 'contact',
        header: 'CONTACT',
    },
    {
        field: 'pax',
        header: 'PAX',
    },
    {
        field: 'unit_price',
        header: 'UNIT PRICE',
        render: (row) => {
            return (<b>{`RM ${Number(row?.unit_price || 0).toLocaleString()}`}</b>);
        }
    },
    {
        field: 'tax',
        header: 'TAX',
        render: (row) => {
            return <TaxChip tax={row?.tax || '-'} />;
        }
    },
    {
        field: 'status',
        header: 'STATUS',
        render: (row) => {
            return <StatusChip status={row?.status || '-'} />;
        }
    },
]

export const merchantPaymentColumns = [
    {
        field: 'id',
        header: 'ORDER ID',
    },
    {
        field: 'payment_type',
        header: 'PAYMENT TYPE',
        render: (row) => {
            return <PaymentChip payment={row?.payment_type || '-'} />;
        }
    },
    {
        field: 'amount',
        header: 'AMOUNT',
        render: (row) => {
            return (<b>{`RM ${Number(row?.amount || 0).toLocaleString()}`}</b>);
        }
    },
    {
        field: 'status',
        header: 'STATUS',
        render: (row) => {
            return <PaymentChip payment={row?.status || '-'} />;
        }
    },
    {
        field: 'created_at',
        header: 'CREATED AT',
    },
];